import React from "react";
import {
    Wheat,
    Pill,
    Shirt,
    Trash2,
    Sparkles,
    Users,
    FlaskConical,
    Utensils,
} from "lucide-react";

const MARKETS = [
    {
        Icon: Wheat,
        name: "Agriculture & Food",
        body: "Biofertilizers, biopesticides, soil-health inputs and food-grade ferments.",
    },
    {
        Icon: Pill,
        name: "Pharma & Healthcare",
        body: "Enzymes, bioactives and fermentation-derived APIs for life-science partners.",
    },
    {
        Icon: Utensils,
        name: "Nutraceuticals & Wellness",
        body: "Plant-based ingredients, traditional-knowledge-backed formulations, probiotics.",
    },
    {
        Icon: Shirt,
        name: "Textile & Leather",
        body: "Enzyme-driven cleaner processing — bleaching, softening, low-impact finishing.",
    },
    {
        Icon: FlaskConical,
        name: "Specialty Chemicals",
        body: "Bio-based alternatives to petrochemical intermediates and surfactants.",
    },
    {
        Icon: Trash2,
        name: "Waste & Environment",
        body: "Effluent treatment, waste-to-value streams, circular bio-economy services.",
    },
    {
        Icon: Sparkles,
        name: "FMCG & Personal Care",
        body: "Natural bioactives and sustainable ingredients for home and body brands.",
    },
    {
        Icon: Users,
        name: "Rural Enterprises & SHGs",
        body: "Village cooperatives, self-help groups and grassroots entrepreneurs.",
    },
];

export const TargetMarket = () => {
    return (
        <section
            id="market"
            data-testid="market-section"
            className="section-pad relative overflow-hidden"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/15"
                style={{ width: 600, height: 600, top: -100, left: -200 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/12"
                style={{ width: 520, height: 520, bottom: -180, right: -160 }}
            />

            <div className="container-x relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            Target market
                        </p>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            Who we build{" "}
                            <em className="not-italic text-primary">for</em>.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                        Yaalee serves a carefully chosen mix of industries — wherever
                        biology can replace harm, reduce waste or create new livelihoods.
                    </p>
                </div>

                {/* Market grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {MARKETS.map(({ Icon, name, body }, i) => (
                        <article
                            key={name}
                            data-testid={`market-card-${i}`}
                            className="group relative rounded-[1.5rem] border border-border bg-card/60 backdrop-blur-sm p-6 overflow-hidden transition-all duration-500 hover:border-primary/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5"
                        >
                            <div
                                aria-hidden
                                className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                            />

                            <div className="relative flex items-start justify-between">
                                <div className="h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-3 tabular-nums">
                                    0{i + 1}
                                </span>
                            </div>

                            <h3 className="mt-7 font-display text-[22px] font-medium tracking-tight leading-tight">
                                {name}
                            </h3>
                            <div className="mt-4 h-px w-10 bg-border group-hover:w-16 group-hover:bg-primary transition-all duration-500" />
                            <p className="mt-4 text-[13.5px] leading-relaxed text-muted-foreground">
                                {body}
                            </p>
                        </article>
                    ))}
                </div>

                {/* Footer strip */}
                <div className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-[1.25rem] border border-border bg-card/60 backdrop-blur-sm px-7 py-6">
                    <p className="font-display text-xl md:text-2xl tracking-tight">
                        Don't see your industry?{" "}
                        <span className="text-muted-foreground">
                            Our R&amp;D is built to adapt.
                        </span>
                    </p>
                    <a
                        href="mailto:founders.yaalee@gmail.com"
                        data-testid="market-contact-cta"
                        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition whitespace-nowrap"
                    >
                        Let's talk →
                    </a>
                </div>
            </div>
        </section>
    );
};

export default TargetMarket;
